import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { CreditNoteFormModal } from '@/components/credit-notes/CreditNoteFormModal';
import { api } from '@/lib/api';
import { editableOf } from '@/lib/credit-notes';
import { useAuth } from '@/lib/use-auth';
import type { CreditNote } from '@/lib/types';

/**
 * A CREDIT NOTE ON A LIST SURFACE — a real link that OPENS THE EDIT FORM when the
 * note may be edited, and is an ordinary link otherwise.
 *
 * The credit-note half of the pattern `InvoiceNumberLink` established; read that
 * file for the link/host split, the early-return click rules and the busy and
 * failure states, all copied rather than re-derived.
 *
 * ── ⛔ EDITABILITY IS `editableOf`, AND NOTHING ELSE ─────────────────────────
 *
 * A note at a non-editable status, or already in Xero, is refused by the server
 * and by the detail page alike; offering the form over it would be a control
 * whose only outcome is a refusal. So the link asks `editableOf` of the note the
 * ROW holds and navigates when it says no — and the host asks again of the note
 * it FETCHED, because the status can move between the list read and the click.
 * A note that turned non-editable in that gap is sent to its detail page rather
 * than shown a form. **No status name and no Xero field is tested here**: a
 * second test at a call site is how two screens come to disagree about one note.
 *
 * ⚠️ **No Xero report.** `CreditNoteFormModal` takes no `onXeroPush`, and a note
 * the form may edit is by construction not in Xero.
 */

interface CreditNoteQuickEditContextValue {
  /** `null` = navigate instead: no host, or this reader may not update credit notes. */
  open: ((creditNoteId: number, label: string) => void) | null;
  pendingId: number | null;
}

const CreditNoteQuickEditContext = createContext<CreditNoteQuickEditContextValue>({
  open: null,
  pendingId: null,
});

export interface CreditNoteLinkProps {
  /** The row's own note — `editableOf` is asked of it before any fetch. */
  creditNote: CreditNote;
  /** What the row shows, verbatim. */
  label: string;
  className?: string;
  title?: string;
}

export function CreditNoteLink({ creditNote, label, className, title }: CreditNoteLinkProps): ReactElement {
  const { open, pendingId } = useContext(CreditNoteQuickEditContext);
  const busy = pendingId === creditNote.id;
  const editable = editableOf(creditNote).editable;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // ⛔ Every one of these is a RETURN: an untouched event is an ordinary link.
    if (open === null || !editable) return;
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    open(creditNote.id, label);
  };

  return (
    <Link
      to={`/credit-notes/${creditNote.id}`}
      className={className}
      {...(title !== undefined ? { title } : {})}
      {...(busy ? { 'aria-busy': true } : {})}
      onClick={handleClick}
    >
      {label}
      {busy && (
        <span aria-hidden="true" className="ml-1 text-fg-subtle">
          …
        </span>
      )}
    </Link>
  );
}

export interface CreditNoteQuickEditProps {
  /** Re-read the surface's own data after a save. Not optional. */
  onSaved: () => void;
  children: ReactNode;
}

/** The one mounted credit-note edit form behind every {@link CreditNoteLink} beneath it. */
export function CreditNoteQuickEdit({ onSaved, children }: CreditNoteQuickEditProps): ReactElement {
  const { ability } = useAuth();
  const navigate = useNavigate();
  /* The form's Save consults no ability, so the gate is here, as on the detail page. */
  const canOpen = ability.canUpdate('credit-notes');

  const [pendingId, setPendingId] = useState<number | null>(null);
  const [failure, setFailure] = useState<{ label: string; message: string } | null>(null);
  const [creditNote, setCreditNote] = useState<CreditNote | null>(null);

  const requestRef = useRef<AbortController | null>(null);
  useEffect(() => () => requestRef.current?.abort(), []);

  const open = useCallback(
    (creditNoteId: number, label: string) => {
      requestRef.current?.abort();
      const controller = new AbortController();
      requestRef.current = controller;

      setFailure(null);
      setPendingId(creditNoteId);

      void api
        .getCreditNote(creditNoteId, controller.signal)
        .then((envelope) => {
          if (controller.signal.aborted) return;
          // The status can move between the list read and this click.
          if (!editableOf(envelope.data).editable) {
            navigate(`/credit-notes/${creditNoteId}`);
            return;
          }
          setCreditNote(envelope.data);
        })
        .catch((caught: unknown) => {
          if (controller.signal.aborted) return;
          setFailure({
            label,
            message: caught instanceof Error ? caught.message : 'The request did not complete.',
          });
        })
        .finally(() => {
          if (controller.signal.aborted) return;
          setPendingId(null);
        });
    },
    [navigate],
  );

  const context = useMemo<CreditNoteQuickEditContextValue>(
    () => ({ open: canOpen ? open : null, pendingId }),
    [canOpen, open, pendingId],
  );

  return (
    <CreditNoteQuickEditContext.Provider value={context}>
      {failure !== null && (
        <Notice
          tone="danger"
          role="alert"
          title={
            <span className="[overflow-wrap:anywhere]">
              Credit note {failure.label} could not be opened.
            </span>
          }
          action={
            <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setFailure(null)}>
              Dismiss
            </Button>
          }
        >
          <span className="[overflow-wrap:anywhere]">{failure.message}</span>
        </Notice>
      )}

      {children}

      {/* Mounted only while a note is open, so every open starts from a fresh baseline. */}
      {creditNote !== null && (
        <CreditNoteFormModal
          open
          creditNote={creditNote}
          onClose={() => setCreditNote(null)}
          onSaved={() => {
            // ⚠️ `onSaved` is a CLOSE; the reload is what matters.
            setCreditNote(null);
            onSaved();
          }}
        />
      )}
    </CreditNoteQuickEditContext.Provider>
  );
}
